import {
  createPracticeLocation,
  deletePracticeLocation,
  updatePracticeLocation
} from 'api/Practice';

import { iaRa } from 'lib/helpers/utility';
import { getSettingsPatch } from 'lib/helpers/practiceSettingsPatch';

import { showAlert } from 'components/shared/Alert/Alert';
import { hasChanges } from 'components/practice/Settings/components/WriteOffCodes/lib/helpers';

export const hasAddressChanged = (currentAddress, initialAddress) => {
  const fields = ['address_ln_1', 'address_ln_2', 'city', 'state', 'zip'];
  return fields.some((field) => currentAddress?.[field] !== initialAddress?.[field]);
};

export const hasPracticeFieldsChanged = (values, initialValues) => {
  return (
    values?.headerPhoto !== initialValues?.headerPhoto ||
    values?.dbaName !== initialValues?.dbaName ||
    values?.addressLabel !== initialValues?.addressLabel ||
    values?.surchargeEnabled !== initialValues?.surchargeEnabled ||
    values?.surchargeAmount !== initialValues?.surchargeAmount ||
    values?.timezone !== initialValues?.timezone ||
    JSON.stringify(values?.displaySettings) !== JSON.stringify(initialValues?.displaySettings) ||
    JSON.stringify(values?.availableTimes) !== JSON.stringify(initialValues?.availableTimes)
  );
};

export const getUpdateFields = (values, initialValues) => {
  const updateFields = {};

  if (values?.headerPhoto !== initialValues?.headerPhoto) {
    updateFields.header_photo = values?.headerPhoto;
  }
  if (values?.dbaName !== initialValues?.dbaName) {
    updateFields.dba_name = values?.dbaName;
  }
  if (values?.addressLabel !== initialValues?.addressLabel) {
    updateFields.address_label = values?.addressLabel;
  }
  if (JSON.stringify(values?.displaySettings) !== JSON.stringify(initialValues?.displaySettings)) {
    updateFields.display_settings = getSettingsPatch(
      initialValues?.displaySettings,
      values?.displaySettings
    );
  }
  if (values?.surchargeEnabled !== initialValues?.surchargeEnabled) {
    updateFields.surcharge_enabled = values?.surchargeEnabled;
    updateFields.surcharge_percentage = values?.surchargeEnabled
      ? parseFloat(values?.surchargeAmount / 100)
      : 0;
  } else if (values?.surchargeAmount !== initialValues?.surchargeAmount) {
    updateFields.surcharge_percentage = values?.surchargeEnabled
      ? parseFloat(values?.surchargeAmount / 100)
      : 0;
  }
  if (
    values?.availableTimes &&
    JSON.stringify(values?.availableTimes) !== JSON.stringify(initialValues?.availableTimes)
  ) {
    updateFields.available_times = JSON.stringify(values.availableTimes);
  }
  if (values?.timezone !== initialValues?.timezone) {
    updateFields.timezone = values.timezone;
  }

  return updateFields;
};

const diffLocations = (values, initialValues) => {
  const currentLocations = iaRa(values?.locations);
  const initialLocations = iaRa(initialValues?.locations);

  return {
    created: currentLocations.filter((location) => !location?.id),
    updated: currentLocations.filter(
      (location) =>
        location?.id &&
        hasChanges(
          initialLocations.find((initial) => initial?.id === location.id),
          location
        )
    ),
    deleted: initialLocations.filter(
      (initial) => !currentLocations.find((location) => location?.id === initial?.id)
    )
  };
};

export const hasLocationsChanged = (values, initialValues) => {
  const { created, updated, deleted } = diffLocations(values, initialValues);
  return created.length > 0 || updated.length > 0 || deleted.length > 0;
};

export const getDuplicateLocationLabel = (values) => {
  const seen = new Set();
  const primaryLabel = String(values?.addressLabel || '')
    .trim()
    .toLowerCase();
  if (primaryLabel) seen.add(primaryLabel);

  for (const location of iaRa(values?.locations)) {
    const label = String(location?.label || '').trim();
    if (!label) continue;
    if (seen.has(label.toLowerCase())) return label;
    seen.add(label.toLowerCase());
  }

  return null;
};

export const handleLocationsSubmit = async (values, initialValues, { navigate }) => {
  const { created, updated, deleted } = diffLocations(values, initialValues);
  let hasError = false;

  const trackFailure = (res) => {
    if (res?.code === 0) return;
    hasError = true;
    showAlert({
      title: 'Practice locations',
      message: res?.error || 'Failed to save practice locations.',
      color: 'danger'
    });
  };

  for (const location of deleted) {
    trackFailure(await deletePracticeLocation(navigate, { id: location.id }));
  }

  for (const location of created) {
    trackFailure(
      await createPracticeLocation(navigate, {
        label: location.label,
        address: location.address
      })
    );
  }

  for (const location of updated) {
    trackFailure(
      await updatePracticeLocation(navigate, {
        id: location.id,
        label: location.label,
        address: location.address
      })
    );
  }

  return !hasError;
};

export const getKeepAsTypedFlags = (updateFields, values) =>
  updateFields?.dba_name !== undefined && values?.keepDbaNameAsTyped
    ? { keep_dba_name_as_typed: true }
    : {};
